import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, User, UserPlus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManualEnrollmentModal = ({ isOpen, onClose, onSuccess, classes }) => {
  const [parents, setParents] = useState([]);
  const [allChildren, setAllChildren] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [isNewParent, setIsNewParent] = useState(false);
  const [existingChildren, setExistingChildren] = useState([]);
  const [selectedExistingChild, setSelectedExistingChild] = useState(null);
  const [isNewChild, setIsNewChild] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Error states for distinguishing "no data" from "request failed"
  const [parentsError, setParentsError] = useState(null);
  const [childrenError, setChildrenError] = useState(null);
  const [parentChildrenError, setParentChildrenError] = useState(null);
  const [loadingParentChildren, setLoadingParentChildren] = useState(false);
  
  const [formData, setFormData] = useState({
    class_id: '',
    parent_email: '',
    parent_name: '',
    parent_phone: '',
    child_name: '',
    child_surname: '',
    child_age: '',
    child_year_group: '',
    child_medical_conditions: '',
    payment_method: 'free', // 'free', 'cash', 'stripe'
    amount_paid: '',
    notes: ''
  });

  // Extract surname from parent's full name for autofill
  const extractSurname = (fullName) => {
    if (!fullName || !fullName.trim()) return '';
    const parts = fullName.trim().split(' ');
    return parts.length >= 2 ? parts[parts.length - 1] : '';
  };

  useEffect(() => {
    if (isOpen) {
      fetchParents();
      fetchAllChildren();
    }
  }, [isOpen]);

  useEffect(() => {
    // Filter parents based on search term - also match children's names
    if (searchTerm.length >= 2) {
      const term = searchTerm.toLowerCase();
      
      // Find parents who match directly or have children that match
      const parentIdsWithMatchingChildren = new Set(
        allChildren
          .filter(c => c.name?.toLowerCase().includes(term))
          .map(c => c.user_id)
      );
      
      const filtered = parents.filter(p => 
        p.email.toLowerCase().includes(term) ||
        p.full_name?.toLowerCase().includes(term) ||
        parentIdsWithMatchingChildren.has(p.id)
      );
      
      // Add info about matching children to results
      const resultsWithChildInfo = filtered.map(p => {
        const matchingChildren = allChildren.filter(c => 
          c.user_id === p.id && c.name?.toLowerCase().includes(term)
        );
        return {
          ...p,
          matchingChildren: matchingChildren.map(c => c.name)
        };
      });
      
      setSearchResults(resultsWithChildInfo.slice(0, 5));
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, parents, allChildren]);

  const fetchParents = async () => {
    setParentsError(null);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setParents(response.data.filter(u => !u.is_admin));
    } catch (error) {
      const message = error.response?.status === 401 
        ? 'Session expired. Please log in again.'
        : 'Failed to load parents. Please try again.';
      setParentsError(message);
      toast.error(message);
    }
  };

  const fetchAllChildren = async () => {
    setChildrenError(null);
    try {
      const response = await axios.get(`${API}/admin/children`);
      setAllChildren(response.data);
    } catch (error) {
      const message = error.response?.status === 401 
        ? 'Session expired. Please log in again.'
        : 'Failed to load children data.';
      setChildrenError(message);
      // Don't toast here - this is background data for search enhancement
    }
  };

  const handleSelectParent = async (parent) => {
    setSelectedParent(parent);
    setIsNewParent(false);
    setParentChildrenError(null);
    setExistingChildren([]);
    setFormData(prev => ({
      ...prev,
      parent_email: parent.email,
      parent_name: parent.full_name || '',
      parent_phone: parent.phone || ''
    }));
    setSearchTerm('');
    setSearchResults([]);
    
    // Fetch children for this parent (children use user_id, not parent_id)
    setLoadingParentChildren(true);
    try {
      const response = await axios.get(`${API}/admin/children`);
      const parentChildren = response.data.filter(c => c.user_id === parent.id);
      setExistingChildren(parentChildren);
      // If parent has existing children, default to selecting one
      if (parentChildren.length > 0) {
        setIsNewChild(false);
      } else {
        setIsNewChild(true);
      }
    } catch (error) {
      const message = error.response?.status === 401 
        ? 'Session expired. Please log in again.'
        : 'Failed to load children for this parent.';
      setParentChildrenError(message);
      toast.error(message);
      setIsNewChild(true); // Default to new child on error
    } finally {
      setLoadingParentChildren(false);
    }
  };

  const handleSelectExistingChild = (child) => {
    setSelectedExistingChild(child);
    setFormData(prev => ({
      ...prev,
      child_name: child.name,
      child_surname: child.surname || '',
      child_age: child.age?.toString() || '',
      child_year_group: child.year_group?.toString() || '',
      child_medical_conditions: child.medical_conditions || ''
    }));
  };

  const handleNewParent = () => {
    setIsNewParent(true);
    setSelectedParent(null);
    setExistingChildren([]);
    setSelectedExistingChild(null);
    setIsNewChild(true);
    setFormData(prev => ({
      ...prev,
      parent_email: searchTerm,
      parent_name: '',
      parent_phone: ''
    }));
  };

  const resetForm = () => {
    setFormData({
      class_id: '',
      parent_email: '',
      parent_name: '',
      parent_phone: '',
      child_name: '',
      child_surname: '',
      child_age: '',
      child_year_group: '',
      child_medical_conditions: '',
      payment_method: 'free',
      amount_paid: '',
      notes: ''
    });
    setSelectedParent(null);
    setIsNewParent(false);
    setExistingChildren([]);
    setSelectedExistingChild(null);
    setIsNewChild(true);
    setSearchTerm('');
    setSearchResults([]);
    setParentsError(null);
    setChildrenError(null);
    setParentChildrenError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.class_id || !formData.parent_email || !formData.child_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const selectedClass = classes.find(c => c.id === formData.class_id);
      
      // Calculate discount based on payment method
      let discountPercent = 100; // Free by default
      let isPaidCash = false;
      let isPaidStripe = false;
      
      if (formData.payment_method === 'cash') {
        discountPercent = 0; // No discount, paid in cash
        isPaidCash = true;
      } else if (formData.payment_method === 'stripe') {
        discountPercent = 0; // No discount, paid via Stripe
        isPaidStripe = true;
      }

      const payload = {
        class_id: formData.class_id,
        school_id: selectedClass?.school_id || '',
        parent_email: formData.parent_email,
        parent_name: formData.parent_name,
        parent_phone: formData.parent_phone,
        child_name: formData.child_name,
        child_surname: formData.child_surname || '',
        child_age: parseInt(formData.child_age) || 6,
        child_year_group: parseInt(formData.child_year_group) || 1,
        child_medical_conditions: formData.child_medical_conditions,
        discount_percent: discountPercent,
        is_free_place: formData.payment_method === 'free',
        is_paid_cash: isPaidCash,
        is_paid_stripe: isPaidStripe,
        notes: formData.notes,
        existing_parent_id: selectedParent?.id || null,
        existing_child_id: selectedExistingChild?.id || null
      };

      await axios.post(`${API}/admin/manual-enrollment`, payload);
      toast.success(`${formData.child_name} enrolled successfully!`);
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create enrollment');
    } finally { setSubmitting(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h2 className="font-heading text-2xl font-bold text-slate-900">Manual Enrollment</h2>
          <button onClick={() => { resetForm(); onClose(); }} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Class Selection */}
          <div>
            <label className="block font-body font-semibold text-slate-700 mb-1">Class *</label>
            <select 
              value={formData.class_id} 
              onChange={(e) => setFormData({...formData, class_id: e.target.value})} 
              className="input-artsy" 
              required 
              data-testid="enroll-class"
            >
              <option value="">Select a class...</option>
              {classes.map(c => (
                <option key={c.id} value={c.id}>{c.name} - {c.day_of_week} {c.time}</option>
              ))}
            </select>
          </div>

          {/* Parent Selection */}
          <div className="border-t pt-4">
            <h3 className="font-heading font-bold text-slate-800 mb-3">Parent Details</h3>
            
            {!selectedParent && !isNewParent ? (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search existing parent by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl font-body focus:ring-2 focus:ring-teal-500"
                    data-testid="parent-search"
                  />
                </div>
                
                {searchResults.length > 0 && (
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    {searchResults.map(parent => (
                      <button
                        key={parent.id}
                        type="button"
                        onClick={() => handleSelectParent(parent)}
                        className="w-full px-4 py-3 text-left hover:bg-teal-50 border-b border-slate-100 last:border-b-0 flex items-center gap-3"
                        data-testid={`select-parent-${parent.id}`}
                      >
                        <User className="w-5 h-5 text-teal-600" />
                        <div className="flex-1">
                          <p className="font-body font-semibold text-slate-900">{parent.full_name || parent.email}</p>
                          <p className="font-body text-sm text-slate-500">{parent.email}</p>
                          {parent.matchingChildren?.length > 0 && (
                            <p className="font-body text-xs text-teal-600 mt-1">
                              Child match: {parent.matchingChildren.join(', ')}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Error state for parent search */}
                {parentsError && (
                  <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="font-body text-sm text-red-700">{parentsError}</p>
                    <button 
                      type="button"
                      onClick={fetchParents}
                      className="text-sm text-red-600 hover:text-red-700 font-semibold mt-1"
                    >
                      Retry
                    </button>
                  </div>
                )}
                
                <button
                  type="button"
                  onClick={handleNewParent}
                  className="w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-600 hover:border-teal-500 hover:text-teal-600 flex items-center justify-center gap-2"
                  data-testid="new-parent-btn"
                >
                  <UserPlus className="w-5 h-5" />
                  Create New Parent Account
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-slate-50 rounded-xl p-3">
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-teal-600" />
                    <div>
                      <p className="font-body font-semibold text-slate-900">
                        {selectedParent ? selectedParent.full_name || selectedParent.email : 'New Parent'}
                      </p>
                      {selectedParent && <p className="font-body text-sm text-slate-500">{selectedParent.email}</p>}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setSelectedParent(null); setIsNewParent(false); setExistingChildren([]); }}
                    className="text-sm text-teal-600 hover:text-teal-700 font-semibold"
                  >
                    Change
                  </button>
                </div>
                
                {isNewParent && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block font-body font-semibold text-slate-700 mb-1">Email *</label>
                      <input 
                        type="email" 
                        value={formData.parent_email} 
                        onChange={(e) => setFormData({...formData, parent_email: e.target.value})} 
                        className="input-artsy" 
                        required 
                        data-testid="enroll-parent-email" 
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Name *</label>
                      <input 
                        type="text" 
                        value={formData.parent_name} 
                        onChange={(e) => setFormData({...formData, parent_name: e.target.value})} 
                        className="input-artsy" 
                        required 
                        data-testid="enroll-parent-name" 
                      />
                    </div>
                    <div>
                      <label className="block font-body font-semibold text-slate-700 mb-1">Phone</label>
                      <input 
                        type="tel" 
                        value={formData.parent_phone} 
                        onChange={(e) => setFormData({...formData, parent_phone: e.target.value})} 
                        className="input-artsy" 
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Child Selection (only show if parent is selected) */}
          {(selectedParent || isNewParent) && (
            <div className="border-t pt-4">
              <h3 className="font-heading font-bold text-slate-800 mb-3">Child Details</h3>
              
              {/* Loading state for parent's children */}
              {loadingParentChildren && (
                <div className="flex items-center gap-2 py-4 text-slate-500">
                  <div className="w-4 h-4 border-2 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                  <span className="font-body text-sm">Loading children...</span>
                </div>
              )}
              
              {/* Error state - distinguish from "no children" */}
              {parentChildrenError && !loadingParentChildren && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="font-body text-sm text-red-700">{parentChildrenError}</p>
                  <button 
                    type="button"
                    onClick={() => handleSelectParent(selectedParent)}
                    className="text-sm text-red-600 hover:text-red-700 font-semibold mt-1"
                  >
                    Retry
                  </button>
                </div>
              )}
              
              {/* Existing children selector (only if parent has children and no error) */}
              {!loadingParentChildren && !parentChildrenError && existingChildren.length > 0 && (
                <div className="mb-4">
                  <div className="flex gap-2 mb-3">
                    <button
                      type="button"
                      onClick={() => { setIsNewChild(false); setSelectedExistingChild(null); }}
                      className={`px-4 py-2 rounded-lg font-body text-sm ${!isNewChild ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      Existing Child ({existingChildren.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => { 
                        setIsNewChild(true); 
                        setSelectedExistingChild(null); 
                        // Autofill surname from parent's name when adding new child
                        const parentName = selectedParent?.full_name || formData.parent_name;
                        const autoSurname = extractSurname(parentName);
                        setFormData(prev => ({
                          ...prev, 
                          child_name: '', 
                          child_surname: autoSurname,
                          child_age: '', 
                          child_year_group: '', 
                          child_medical_conditions: ''
                        })); 
                      }}
                      className={`px-4 py-2 rounded-lg font-body text-sm ${isNewChild ? 'bg-teal-600 text-white' : 'bg-slate-100 text-slate-700'}`}
                    >
                      New Child
                    </button>
                  </div>
                  
                  {!isNewChild && (
                    <div className="grid grid-cols-2 gap-2">
                      {existingChildren.map(child => (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => handleSelectExistingChild(child)}
                          className={`p-3 rounded-xl border-2 text-left transition-colors ${
                            selectedExistingChild?.id === child.id 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-slate-200 hover:border-teal-300'
                          }`}
                          data-testid={`select-child-${child.id}`}
                        >
                          <p className="font-body font-semibold text-slate-900">{child.name} {child.surname || ''}</p>
                          <p className="font-body text-sm text-slate-500">Year {child.year_group}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* No children message - only show when no error and no existing children */}
              {!loadingParentChildren && !parentChildrenError && existingChildren.length === 0 && selectedParent && !isNewParent && (
                <p className="font-body text-sm text-slate-500 mb-4">
                  This parent has no registered children. Add a new child below.
                </p>
              )}
              
              {/* Child form fields */}
              {!loadingParentChildren && (isNewChild || selectedExistingChild) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body font-semibold text-slate-700 mb-1">First Name *</label>
                    <input 
                      type="text" 
                      value={formData.child_name} 
                      onChange={(e) => setFormData({...formData, child_name: e.target.value})} 
                      className="input-artsy" 
                      disabled={!!selectedExistingChild}
                      required 
                      data-testid="enroll-child-name" 
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold text-slate-700 mb-1">
                      Surname {!selectedExistingChild && <span className="text-slate-400 text-xs font-normal">(auto-filled)</span>}
                    </label>
                    <input 
                      type="text" 
                      value={formData.child_surname} 
                      onChange={(e) => setFormData({...formData, child_surname: e.target.value})} 
                      className="input-artsy" 
                      disabled={!!selectedExistingChild}
                      placeholder={selectedParent ? extractSurname(selectedParent.full_name) : ''}
                      data-testid="enroll-child-surname" 
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold text-slate-700 mb-1">Age *</label>
                    <input 
                      type="number" 
                      min="4" max="12" 
                      value={formData.child_age} 
                      onChange={(e) => setFormData({...formData, child_age: e.target.value})} 
                      className="input-artsy" 
                      required 
                      data-testid="enroll-child-age" 
                    />
                  </div>
                  <div>
                    <label className="block font-body font-semibold text-slate-700 mb-1">
                      Year Group * {selectedExistingChild && <span className="text-slate-400 text-xs font-normal">(editable)</span>}
                    </label>
                    <select 
                      value={formData.child_year_group} 
                      onChange={(e) => setFormData({...formData, child_year_group: e.target.value})} 
                      className="input-artsy" 
                      required 
                      data-testid="enroll-child-year"
                    >
                      <option value="">Select...</option>
                      {[1,2,3,4,5,6].map(y => <option key={y} value={y}>Year {y}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Payment Method */}
          {(selectedParent || isNewParent) && (isNewChild || selectedExistingChild) && (
            <div className="border-t pt-4">
              <h3 className="font-heading font-bold text-slate-800 mb-3">Payment Method</h3>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({...formData, payment_method: 'free'})}
                  className={`p-4 rounded-xl border-2 text-center transition-colors ${
                    formData.payment_method === 'free' 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-slate-200 hover:border-teal-300'
                  }`}
                  data-testid="payment-free"
                >
                  <p className="font-body font-bold text-slate-900">Free Place</p>
                  <p className="font-body text-sm text-slate-500">£0</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, payment_method: 'cash'})}
                  className={`p-4 rounded-xl border-2 text-center transition-colors ${
                    formData.payment_method === 'cash' 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-slate-200 hover:border-teal-300'
                  }`}
                  data-testid="payment-cash"
                >
                  <p className="font-body font-bold text-slate-900">Cash</p>
                  <p className="font-body text-sm text-slate-500">Paid in hand</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({...formData, payment_method: 'stripe'})}
                  className={`p-4 rounded-xl border-2 text-center transition-colors ${
                    formData.payment_method === 'stripe' 
                      ? 'border-teal-500 bg-teal-50' 
                      : 'border-slate-200 hover:border-teal-300'
                  }`}
                  data-testid="payment-stripe"
                >
                  <p className="font-body font-bold text-slate-900">Stripe</p>
                  <p className="font-body text-sm text-slate-500">Already paid online</p>
                </button>
              </div>
              
              <div className="mt-4">
                <label className="block font-body font-semibold text-slate-700 mb-1">Notes (optional)</label>
                <input 
                  type="text" 
                  value={formData.notes} 
                  onChange={(e) => setFormData({...formData, notes: e.target.value})} 
                  className="input-artsy" 
                  placeholder="e.g., Scholarship, Staff child, Cash paid 01/12" 
                />
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button 
              type="button" 
              onClick={() => { resetForm(); onClose(); }} 
              className="btn-outline flex-1"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={submitting || (!selectedParent && !isNewParent)} 
              className="btn-primary flex-1" 
              data-testid="enroll-submit"
            >
              {submitting ? 'Creating...' : 'Create Enrollment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualEnrollmentModal;
